/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColumnSort, TableDataWithImage } from '@spo/logic';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { VpLoader } from '@vtmn-play/react';
import { clsx } from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRoles } from '../../../contexts/RolesContext';
import { Footer } from '../../footer';
import { SMTableView } from './SMTableView';
import './smTable.scss';

interface SMTableProps {
  data: TableDataWithImage[];
  columnSort: ColumnSort;
  setColumnSort: (columnSort: ColumnSort) => void;
  columns: any;
  isLoading: boolean;
  defaultSMIds: string[];
}

export const SMTable = ({
  data,
  columnSort,
  setColumnSort,
  columns,
  isLoading,
  defaultSMIds,
}: SMTableProps) => {
  const [selectedSmIds, setSelectedSmIds] = useState<string[]>(defaultSMIds);
  const [globalChecked, setGlobalChecked] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasRoleProductLeader } = useRoles();

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const tableScrollRef = useRef(null);
  const { rows } = table.getRowModel();

  const selectSmId = (newSmId: string) => {
    const shadowSmIds = [...selectedSmIds];
    if (!selectedSmIds.includes(newSmId)) {
      shadowSmIds.push(newSmId);
    } else {
      shadowSmIds.splice(shadowSmIds.indexOf(newSmId), 1);
    }
    setSelectedSmIds(shadowSmIds);
  };

  const handleMassEdit = () => {
    navigate(`/personal-offer/mass-edit?smIds=${selectedSmIds.join(',')}`);
  };

  const cancelSelection = () => {
    setSelectedSmIds([]);
    setGlobalChecked(false);
  };

  const actualPageSmIds = rows.map((row) => row.original.id);

  const handleGlobalSmSelection = (selectedStatus: boolean) => {
    if (selectedStatus) {
      setSelectedSmIds([...new Set([...selectedSmIds, ...actualPageSmIds])]);
    } else {
      const cleanedList = [...selectedSmIds].filter(
        (value) => !actualPageSmIds.includes(value),
      );
      setSelectedSmIds(cleanedList);
    }
    setGlobalChecked(selectedStatus);
  };

  useEffect(() => {
    setGlobalChecked(
      selectedSmIds.some((selectedSmId) =>
        actualPageSmIds.includes(selectedSmId),
      ),
    );
  }, [selectedSmIds, actualPageSmIds]);

  return (
    <div
      className={clsx('sm-table', {
        'with-footer': Boolean(selectedSmIds.length),
      })}
      ref={tableScrollRef}
    >
      {isLoading ? (
        <div data-testid="sm-table-loader" className="loader">
          <VpLoader size="medium" />
        </div>
      ) : (
        <>
          <SMTableView
            table={table}
            columnSort={columnSort}
            setColumnSort={setColumnSort}
            globalChecked={globalChecked}
            handleGlobalSmSelection={handleGlobalSmSelection}
            selectedSmIds={selectedSmIds}
            selectSmId={selectSmId}
          />
          {Boolean(selectedSmIds.length) && (
            <Footer
              ctaLabel={t('footer.edit-button')}
              validateAction={handleMassEdit}
              cancelAction={cancelSelection}
              isValidateActionDisabled={!hasRoleProductLeader}
              comment={`${selectedSmIds.length} ${t(
                'footer.comment.selected-sm',
              )}`}
              tooltipComment={
                !hasRoleProductLeader ? t('no-access-page.edit-button') : ''
              }
            />
          )}
        </>
      )}
    </div>
  );
};
